import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  VStack,
  SimpleGrid,
  Button,
  Select,
  Switch,
  Text,
  Divider,
  HStack,
  useToast,
} from '@chakra-ui/react';
import { getData } from 'utils/httpClient';
import { getUserById, createUser, updateUser } from 'services/userManagement.service';
import DocumentInput from 'components/common/DocumentInput';

const emptyAddress = {
  street: '',
  number: '',
  apartment: '',
  zipCode: '',
  city: '',
  country: 'Uruguay',
  locality: '',
};

const emptyPerson = {
  name: '',
  secondName: '',
  lastName: '',
  maternalLastName: '',
  documentTypeId: '',
  document: '',
  phone: '',
  cellPhone: '',
  birthPlace: '',
  birthDate: '',
  sexId: '',
  email: '',
  address: { ...emptyAddress },
  profession: '',
  emergencyContact: '',
  emergencyContactPhone: '',
  medicalInsurance: '',
  observations: '',
};

const emptyForm = {
  username: '',
  password: '',
  enabled: true,
  profiles: { student: false, teacher: false },
  roleIds: [],
  person: { ...emptyPerson, address: { ...emptyAddress } },
  employee: { employeeTypeId: 2, employeeTitle: '', salary: '' },
};

function buildPayload(form, isEdit) {
  const payload = {
    username: form.username || form.person.document,
    enabled: form.enabled,
    profiles: form.profiles,
    person: {
      ...form.person,
      documentTypeId: Number(form.person.documentTypeId),
      sexId: Number(form.person.sexId),
    },
    roleIds: form.roleIds,
  };

  if (form.profiles.teacher) {
    payload.employee = {
      employeeTypeId: Number(form.employee.employeeTypeId) || 2,
      employeeTitle: form.employee.employeeTitle || undefined,
      salary: form.employee.salary ? Number(form.employee.salary) : undefined,
    };
  }

  if (!isEdit && form.password) {
    payload.password = form.password;
  } else if (isEdit && form.password) {
    payload.password = form.password;
  }

  return payload;
}

export default function UserFormModal({
  isOpen,
  onClose,
  editUserId,
  roles = [],
  onSaved,
}) {
  const toast = useToast();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [sexTypes, setSexTypes] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    loadCatalogs();
    if (editUserId) {
      loadUser(editUserId);
    } else {
      setForm({
        ...emptyForm,
        person: { ...emptyPerson, address: { ...emptyAddress } },
      });
    }
  }, [isOpen, editUserId]);

  const loadCatalogs = async () => {
    try {
      const [docRes, sexRes, empRes] = await Promise.all([
        getData('/documentTypes'),
        getData('/sexTypes'),
        getData('/employeeTypes'),
      ]);
      setDocumentTypes(docRes?.payload?.documentTypes || []);
      setSexTypes(sexRes?.payload?.sexTypes || []);
      setEmployeeTypes(empRes?.payload?.employeeTypes || []);
    } catch {
      // catálogos opcionales para no bloquear el modal
    }
  };

  const loadUser = async (id) => {
    try {
      setLoading(true);
      const res = await getUserById(id);
      const data = res?.payload?.data;
      if (!data) return;

      setForm({
        username: data.username || '',
        password: '',
        enabled: data.enabled,
        profiles: {
          student: data.profiles?.student || false,
          teacher: data.profiles?.teacher || false,
        },
        roleIds: data.roleIds || [],
        person: {
          name: data.person?.name || '',
          secondName: data.person?.secondName || '',
          lastName: data.person?.lastName || '',
          maternalLastName: data.person?.maternalLastName || '',
          documentTypeId: data.person?.documentTypeId || '',
          document: data.person?.document || '',
          phone: data.person?.phone || '',
          cellPhone: data.person?.cellPhone || '',
          birthPlace: data.person?.birthPlace || '',
          birthDate: data.person?.birthDate ? data.person.birthDate.slice(0, 10) : '',
          sexId: data.person?.sexId || '',
          email: data.person?.email || '',
          address: data.person?.address || { ...emptyAddress },
          profession: data.person?.profession || '',
          emergencyContact: data.person?.emergencyContact || '',
          emergencyContactPhone: data.person?.emergencyContactPhone || '',
          medicalInsurance: data.person?.medicalInsurance || '',
          observations: data.person?.observations || '',
        },
        employee: {
          employeeTypeId: data.employee?.employeeTypeId || 2,
          employeeTitle: data.employee?.employeeTitle || '',
          salary: data.employee?.salary ?? '',
        },
      });
    } catch (error) {
      toast({
        title: 'Error al cargar usuario',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
        duration: 4000,
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const setPersonField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      person: { ...prev.person, [field]: value },
    }));
  };

  const setAddressField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      person: {
        ...prev.person,
        address: { ...prev.person.address, [field]: value },
      },
    }));
  };

  const toggleProfile = (key) => {
    setForm((prev) => ({
      ...prev,
      profiles: { ...prev.profiles, [key]: !prev.profiles[key] },
    }));
  };

  const toggleRoleId = (roleId) => {
    setForm((prev) => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter((id) => id !== roleId)
        : [...prev.roleIds, roleId],
    }));
  };

  const handleSave = async () => {
    if (!form.person.name || !form.person.lastName || !form.person.document) {
      toast({ title: 'Complete los datos obligatorios de la persona', status: 'warning', duration: 3000 });
      return;
    }
    if (!editUserId && !form.password && !form.person.document) {
      toast({ title: 'Indique una contraseña o use el documento por defecto', status: 'warning', duration: 3000 });
    }

    try {
      setSaving(true);
      const payload = buildPayload(form, Boolean(editUserId));
      if (editUserId) {
        await updateUser(editUserId, payload);
        toast({ title: 'Usuario actualizado', status: 'success', duration: 3000 });
      } else {
        if (!payload.password) payload.password = form.person.document;
        await createUser(payload);
        toast({ title: 'Usuario creado', status: 'success', duration: 3000 });
      }
      onSaved?.();
      onClose();
    } catch (error) {
      toast({
        title: 'Error al guardar',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>{editUserId ? 'Editar usuario' : 'Nuevo usuario'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!loading && (
            <VStack spacing={5} align="stretch">
              <HStack justify="space-between">
                <FormControl display="flex" alignItems="center" w="auto">
                  <FormLabel mb={0}>Acceso habilitado</FormLabel>
                  <Switch
                    isChecked={form.enabled}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    colorScheme="brand"
                  />
                </FormControl>
              </HStack>

              <Text fontWeight="600" fontSize="sm" color="gray.600">Roles de acceso</Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                {roles.map((role) => (
                  <Checkbox
                    key={role.id}
                    isChecked={form.roleIds.includes(role.id)}
                    onChange={() => toggleRoleId(role.id)}
                  >
                    {role.role}
                  </Checkbox>
                ))}
              </SimpleGrid>

              <Text fontWeight="600" fontSize="sm" color="gray.600">Perfiles</Text>
              <HStack spacing={6} wrap="wrap">
                <Checkbox
                  isChecked={form.profiles.student}
                  onChange={() => toggleProfile('student')}
                >
                  Estudiante
                </Checkbox>
                <Checkbox
                  isChecked={form.profiles.teacher}
                  onChange={() => toggleProfile('teacher')}
                >
                  Docente
                </Checkbox>
              </HStack>

              <Divider />

              <Text fontWeight="600" fontSize="sm" color="gray.600">Cuenta de acceso</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Usuario</FormLabel>
                  <Input
                    placeholder="Por defecto: documento"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{editUserId ? 'Nueva contraseña (opcional)' : 'Contraseña'}</FormLabel>
                  <Input
                    type="password"
                    placeholder={editUserId ? 'Dejar vacío para no cambiar' : 'Por defecto: documento'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </FormControl>
              </SimpleGrid>

              <Divider />

              <Text fontWeight="600" fontSize="sm" color="gray.600">Datos personales</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Nombre</FormLabel>
                  <Input value={form.person.name} onChange={(e) => setPersonField('name', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Segundo nombre</FormLabel>
                  <Input value={form.person.secondName} onChange={(e) => setPersonField('secondName', e.target.value)} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Apellido</FormLabel>
                  <Input value={form.person.lastName} onChange={(e) => setPersonField('lastName', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Segundo apellido</FormLabel>
                  <Input value={form.person.maternalLastName} onChange={(e) => setPersonField('maternalLastName', e.target.value)} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Tipo de documento</FormLabel>
                  <Select
                    value={form.person.documentTypeId}
                    onChange={(e) => setPersonField('documentTypeId', e.target.value)}
                  >
                    <option value="">Seleccione</option>
                    {documentTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.description}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Documento</FormLabel>
                  <DocumentInput value={form.person.document} onChange={(e) => setPersonField('document', e.target.value)} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Sexo</FormLabel>
                  <Select value={form.person.sexId} onChange={(e) => setPersonField('sexId', e.target.value)}>
                    <option value="">Seleccione</option>
                    {sexTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.description}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Fecha de nacimiento</FormLabel>
                  <Input type="date" value={form.person.birthDate} onChange={(e) => setPersonField('birthDate', e.target.value)} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Celular</FormLabel>
                  <Input value={form.person.cellPhone} onChange={(e) => setPersonField('cellPhone', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Teléfono</FormLabel>
                  <Input value={form.person.phone} onChange={(e) => setPersonField('phone', e.target.value)} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input type="email" value={form.person.email} onChange={(e) => setPersonField('email', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Lugar de nacimiento</FormLabel>
                  <Input value={form.person.birthPlace} onChange={(e) => setPersonField('birthPlace', e.target.value)} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Contacto de emergencia</FormLabel>
                  <Input value={form.person.emergencyContact} onChange={(e) => setPersonField('emergencyContact', e.target.value)} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Tel. emergencia</FormLabel>
                  <Input value={form.person.emergencyContactPhone} onChange={(e) => setPersonField('emergencyContactPhone', e.target.value)} />
                </FormControl>
              </SimpleGrid>

              <Text fontWeight="600" fontSize="sm" color="gray.600">Dirección</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Calle</FormLabel>
                  <Input value={form.person.address.street} onChange={(e) => setAddressField('street', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Número</FormLabel>
                  <Input value={form.person.address.number} onChange={(e) => setAddressField('number', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Ciudad</FormLabel>
                  <Input value={form.person.address.city} onChange={(e) => setAddressField('city', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>País</FormLabel>
                  <Input value={form.person.address.country} onChange={(e) => setAddressField('country', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Localidad</FormLabel>
                  <Input value={form.person.address.locality} onChange={(e) => setAddressField('locality', e.target.value)} />
                </FormControl>
              </SimpleGrid>

              {form.profiles.teacher && (
                <>
                  <Divider />
                  <Text fontWeight="600" fontSize="sm" color="gray.600">Datos de docente</Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>Tipo de empleado</FormLabel>
                      <Select
                        value={form.employee.employeeTypeId}
                        onChange={(e) => setForm({
                          ...form,
                          employee: { ...form.employee, employeeTypeId: e.target.value },
                        })}
                      >
                        {employeeTypes.map((t) => (
                          <option key={t.id} value={t.id}>{t.description}</option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Título / cargo</FormLabel>
                      <Input
                        value={form.employee.employeeTitle}
                        onChange={(e) => setForm({
                          ...form,
                          employee: { ...form.employee, employeeTitle: e.target.value },
                        })}
                      />
                    </FormControl>
                  </SimpleGrid>
                </>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
          <Button colorScheme="brand" onClick={handleSave} isDisabled={loading}>
            Guardar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
